import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { Loader2 } from 'lucide-react';
import { cn } from '../../../../shared';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { sendCandidateEmail } from '../../services/taOutboundApi';
import { addCandidateNote } from '../../services/taCandidateNotesApi';
import {
  requestNewBookingLink,
  type NewLinkDraft,
  type NewLinkReason,
} from '../../services/bookingNewLinkApi';
import { RescheduleBody } from '../InterviewRescheduleButton';
import { useOptionalGmailConnection } from './GmailConnectionContext';
import { LanguageToggle } from './LanguageToggle';
import { ModalActions } from './ModalActions';
import type { PendingAction } from './usePendingActions';
import { noShowLine, type PipelineRow } from './pipelineRows';

type Lang = 'de' | 'en';
type Method = 'link' | 'time';

/**
 * Reschedule a booked opening interview (Theresa, Oct 19 2026).
 *
 * Two reasons and, for each, two ways — decisions 1A–3A:
 *  · No-show → a NEW booking link for the same stage, or pick a time for them.
 *  · Move the interview → a link to pick a new time themselves (their own manage page, so the
 *    slot is held until they do), or pick a time for them.
 * "Pick a time" is the Log's own Move dialog, unchanged. "Send a link" drafts the email from the
 * Library ("no-show" / "reschedule"), sends from the recruiter's Gmail behind the board's ten-second
 * countdown, then leaves a Lever note so the hiring team sees why the round moved.
 */
export function RescheduleDialog({
  row,
  postingId,
  boardEmail,
  onClose,
  onChanged,
  onDefer,
  surface = 'board',
}: {
  row: PipelineRow;
  postingId: string;
  boardEmail: string;
  onClose: () => void;
  onChanged?: () => void;
  onDefer?: (action: Omit<PendingAction, 'secondsLeft'>) => void;
  /** Where the dialog was opened from, so the two are separable in analytics. */
  surface?: 'board' | 'log';
}) {
  // A past interview can only have been missed; a future one can only be moved.
  const [reason, setReason] = useState<NewLinkReason>(row.past ? 'no_show' : 'move');
  const [method, setMethod] = useState<Method>('link');
  const [draft, setDraft] = useState<NewLinkDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickTime, setPickTime] = useState(false);

  if (!row.bookingRef) return null;

  if (draft) {
    return (
      <DraftEditor
        row={row}
        postingId={postingId}
        boardEmail={boardEmail}
        surface={surface}
        draft={draft}
        // A no-show link exists from the moment it was created, so the row should say so even if
        // no email goes. Refreshing NOW would reload the board under the open draft; on close.
        onClose={() => {
          onClose();
          if (draft.reason === 'no_show') onChanged?.();
        }}
        onChanged={onChanged}
        onDefer={onDefer}
      />
    );
  }

  if (pickTime) {
    return (
      <RescheduleBody
        bookingRef={row.bookingRef}
        candidateName={row.name}
        currentStart={row.start}
        onClose={onClose}
        onRescheduled={() => onChanged?.()}
        surface={surface}
      />
    );
  }

  const createLink = async () => {
    setBusy(true);
    setError(null);
    try {
      const d = await requestNewBookingLink(row.bookingRef as string, reason);
      void logAnalyticsEvent('ta_reschedule_link_created', { reason, surface });
      setDraft(d);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Could not create a new link.');
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (method === 'link') void createLink();
    else setPickTime(true);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">Reschedule {row.name}</h2>
      {row.noShow && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {noShowLine(row.noShow)}
        </p>
      )}
      <Choice
        label="Why?"
        value={reason}
        onChange={(v) => {
          setReason(v as NewLinkReason);
          setError(null);
        }}
        options={[
          {
            value: 'no_show',
            label: 'No-show',
            hint: 'They did not come to the interview.',
            disabled: !row.past,
            disabledHint: 'The interview has not happened yet.',
          },
          {
            value: 'move',
            label: 'Move the interview',
            hint: 'The date or time has to change.',
            disabled: row.past,
            disabledHint: 'The interview time has passed — choose No-show.',
          },
        ]}
      />
      <Choice
        label="How?"
        value={method}
        onChange={(v) => setMethod(v as Method)}
        options={[
          {
            value: 'link',
            label: reason === 'no_show' ? 'Send a new booking link' : 'Send a link to pick a new time',
            hint:
              reason === 'no_show'
                ? 'A fresh link for the same stage, with the “No-show” email from the Email Library.'
                : 'Their own link to move it. The current slot stays booked until they pick a new one.',
          },
          {
            value: 'time',
            label: 'Pick a time for them',
            hint: 'You choose the new slot (e.g. agreed by phone); they get the new time by email.',
          },
        ]}
      />
      {error && <p className="text-sm text-red-700">{error}</p>}
      <ModalActions
        confirmLabel={busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Next'}
        confirmDisabled={busy}
        onConfirm={next}
        onCancel={onClose}
      />
    </div>
  );
}

function Choice({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string; hint: string; disabled?: boolean; disabledHint?: string }[];
}) {
  return (
    <fieldset className="space-y-1.5">
      <legend className="mb-1 text-sm font-medium text-gray-700">{label}</legend>
      {options.map((o) => (
        <label
          key={o.value}
          className={cn(
            'flex cursor-pointer gap-2 rounded-lg border px-3 py-2 text-sm',
            value === o.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200',
            o.disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <input
            type="radio"
            className="mt-1"
            checked={value === o.value}
            disabled={o.disabled}
            onChange={() => onChange(o.value)}
          />
          <span>
            <span className="font-medium text-gray-900">{o.label}</span>
            <span className="block text-xs text-gray-500">
              {o.disabled && o.disabledHint ? o.disabledHint : o.hint}
            </span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/** The drafted email: edit, then send from the recruiter's Gmail behind the countdown. */
function DraftEditor({
  row,
  postingId,
  boardEmail,
  surface,
  draft,
  onClose,
  onChanged,
  onDefer,
}: {
  row: PipelineRow;
  postingId: string;
  boardEmail: string;
  surface: 'board' | 'log';
  draft: NewLinkDraft;
  onClose: () => void;
  onChanged?: () => void;
  onDefer?: (action: Omit<PendingAction, 'secondsLeft'>) => void;
}) {
  const gmail = useOptionalGmailConnection();
  const [lang, setLang] = useState<Lang>(draft.language);
  const [subject, setSubject] = useState({ de: draft.email.de.subject, en: draft.email.en.subject });
  const [body, setBody] = useState({ de: draft.email.de.body, en: draft.email.en.body });
  const canSend = Boolean(gmail?.connected && draft.opportunityId && onDefer);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${subject[lang]}\n\n${body[lang]}`);
      showToast({ status: 'success', description: 'Email copied to clipboard.' });
    } catch {
      showToast({ status: 'error', description: 'Could not copy — select and copy manually.' });
    }
  };

  const send = () => {
    if (!canSend || !onDefer || !draft.opportunityId) return;
    const opportunityId = draft.opportunityId;
    const s = subject[lang];
    const b = body[lang];
    onDefer({
      id: `reschedule-link:${row.bookingRef}`,
      label: `Sending ${row.name} a new interview link`,
      run: () => {
        void (async () => {
          try {
            const r = await sendCandidateEmail(
              {
                opportunityId,
                subject: s,
                body: b,
                kind: 'link-resend',
                roleTitle: draft.roleTitle,
                stageName: draft.stageName,
                postingId,
              },
              gmail?.token ?? null
            );
            showToast({ status: 'success', description: `New interview link sent to ${r.to}.` });
            void logAnalyticsEvent('ta_reschedule_link_sent', { reason: draft.reason, surface });
            // Best effort: the email went, which is what matters; a failed note is said, not hidden.
            try {
              await addCandidateNote({ opportunityId, boardEmail, value: draft.note });
            } catch {
              showToast({ status: 'warning', description: 'Sent — but the Lever note could not be added.' });
            }
            onChanged?.();
          } catch (e) {
            showToast({
              status: 'error',
              description: `Not sent: ${e instanceof Error && e.message ? e.message : 'unknown error'} — the link still works, so you can copy the email and send it yourself.`,
            });
          }
        })();
      },
      undo: () =>
        showToast({
          status: 'info',
          description: `Stopped — no email went to ${row.name}.${draft.reason === 'no_show' ? ' The new link still exists.' : ''}`,
        }),
    });
    onClose();
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">
        {draft.reason === 'no_show' ? 'New booking link' : 'Link to pick a new time'} for {row.name}
      </h2>
      {!draft.fromLibrary && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          The Email Library template for this is missing or switched off, so a short plain email is
          drafted instead.
        </p>
      )}
      <LanguageToggle value={lang} onChange={(v: Lang) => setLang(v)} />
      <Input
        label="Subject"
        value={subject[lang]}
        onChange={(e) => setSubject({ ...subject, [lang]: (e.target as HTMLInputElement).value })}
      />
      <Textarea
        rows={5}
        value={body[lang]}
        onChange={(e) => setBody({ ...body, [lang]: (e.target as HTMLTextAreaElement).value })}
        className="min-h-[16rem] text-[13px]"
      />
      <p className="text-xs text-gray-500">
        After sending, a note is added in Lever: “{draft.note}”
      </p>
      {!canSend && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-amber-800">
          <span>Connect Gmail to send from here — or copy the email and send it yourself.</span>
          {gmail && !gmail.connected && (
            <Button size="sm" variant="outline" loading={gmail.connecting} onClick={() => void gmail.connect()}>
              Connect Gmail
            </Button>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button variant="outline" onClick={copy}>
          Copy email
        </Button>
        <Button onClick={send} disabled={!canSend}>
          Send
        </Button>
      </div>
    </div>
  );
}
